import { UserInfo } from '@future-harry/nxt-styles';
import { defineStore } from 'pinia';
import { USER_INFO_ENDPOINTS } from './user-info.endpoints';
import { useLanguagesStore } from '@/core/collections/languages/languages.store';
import { getLanguages } from '@/core/collections/languages/languages.service';
import { ApiResource } from '@future-harry/nxt-styles';
import { Language } from '@future-harry/nxt-vue-library';

export interface ApiPatchObject {
   op: 'replace';
   path: string;
   value: any;
}

export type ApiPatch = Array<ApiPatchObject>;

export const useUserInfoStore = defineStore('user-info', () => {
   const userInfo = ref<UserInfo | null>(null);
   const loadingUserInfo = ref(true);
   const permissions = ref<object>({});
   const authError = ref<number | null>(null);
   const languagesStore = useLanguagesStore();

   async function getUserInfo() {
      try {
         loadingUserInfo.value = true;
         authError.value = null;
         const { data: userInfoResponse } = await useFetch<UserInfo>(USER_INFO_ENDPOINTS.GET(), {
            transformApiRequest: true,
         });
         userInfo.value = userInfoResponse.value || null;

         // fetch languages only when user info is loaded once
         const languages = await getLanguages();
         languagesStore.setLanguages(languages);
      } catch (error: any) {
         userInfo.value = null;
         const status = error?.response?.status || error?.status;
         if (status === 403) {
            authError.value = 403;
         }
      }
   }

   async function patchUserInfo(userInfoPatchData: ApiPatch) {
      const response = await http.patch<ApiResource<UserInfo>>(`/user-info`, userInfoPatchData);
      return response.data?.data;
   }

   async function changeUserInfo(userInfoPatchData: ApiPatch) {
      userInfo.value = await patchUserInfo(userInfoPatchData);
      return userInfo.value;
   }

   async function changeUserInfoLanguage(language: Language) {
      if (!userInfo.value) return;

      const patchData: ApiPatch = [{ op: 'replace', path: 'language_id', value: language.id }];

      const oldUserInfo = { ...userInfo.value };
      userInfo.value = { ...userInfo.value, language: language };

      patchUserInfo(patchData).catch(() => (userInfo.value = oldUserInfo));
   }

   return {
      userInfo,
      loadingUserInfo,
      permissions,
      authError,
      getUserInfo,
      changeUserInfo,
      changeUserInfoLanguage,
   };
});
